/**
 * EditModal
 *
 * A focused modal for editing a single element clicked in the preview.
 * - Text/heading elements: contentEditable div with B/I/U formatting toolbar
 * - Image elements: URL input with preview
 * - Button elements: single-line text input
 *
 * Uses innerHTML for text so <strong>/<em>/<u> tags carry through to the preview.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { refineText } from '../../api/staging';
import type { RefinementType } from '../../api/staging';

// =============================================================================
// TYPES
// =============================================================================

export interface EditModalProps {
  /** What kind of element is being edited */
  elementKind: 'heading' | 'text' | 'image' | 'button';
  /** Current value of the element (may include HTML tags) */
  currentValue: string;
  /** Human-readable label for the element */
  label: string;
  /** Website ID for AI text refinement */
  websiteId?: string;
  /** Element label for AI context */
  elementLabel?: string;
  /** Editor mode — 'customer' hides AI refinement features */
  mode?: 'admin' | 'customer';
  /** Called with the new value when saved */
  onSave: (newValue: string) => void;
  /** Called when modal is closed without saving */
  onClose: () => void;
}

// =============================================================================
// CONFIG
// =============================================================================

const KIND_CONFIG: Record<string, { icon: string; placeholder: string }> = {
  heading: { icon: 'H', placeholder: 'Enter heading text...' },
  text: { icon: 'T', placeholder: 'Enter text content...' },
  image: { icon: '\u{1F5BC}', placeholder: 'Enter image URL...' },
  button: { icon: '\u25B6', placeholder: 'Enter button text...' },
};

/** Whether this element kind supports rich text formatting */
function supportsFormatting(kind: string): boolean {
  return kind === 'text' || kind === 'heading';
}

// =============================================================================
// FORMATTING TOOLBAR
// =============================================================================

interface FormatButtonProps {
  command: string;
  icon: React.ReactNode;
  title: string;
  editorRef: React.RefObject<HTMLDivElement | null>;
}

function FormatButton({ command, icon, title, editorRef }: FormatButtonProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Restore focus to the editor before executing command
    editorRef.current?.focus();
    document.execCommand(command, false);
  };

  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()} // Prevent stealing focus
      onClick={handleClick}
      title={title}
      className="w-7 h-7 flex items-center justify-center rounded text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition-colors text-sm"
    >
      {icon}
    </button>
  );
}

function FormattingToolbar({ editorRef }: { editorRef: React.RefObject<HTMLDivElement | null> }) {
  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 bg-gray-50 rounded-t-lg">
      <FormatButton
        command="bold"
        icon={<span className="font-bold">B</span>}
        title="Bold (⌘B)"
        editorRef={editorRef}
      />
      <FormatButton
        command="italic"
        icon={<span className="italic">I</span>}
        title="Italic (⌘I)"
        editorRef={editorRef}
      />
      <FormatButton
        command="underline"
        icon={<span className="underline">U</span>}
        title="Underline (⌘U)"
        editorRef={editorRef}
      />
      <div className="w-px h-4 bg-gray-300 mx-1" />
      <FormatButton
        command="removeFormat"
        icon={
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        }
        title="Clear formatting"
        editorRef={editorRef}
      />
    </div>
  );
}

// =============================================================================
// AI TEXT REFINEMENT BUTTONS
// =============================================================================

const REFINEMENT_OPTIONS: { type: RefinementType; label: string }[] = [
  { type: 'shorter', label: 'Shorter' },
  { type: 'longer', label: 'Longer' },
  { type: 'friendlier', label: 'Friendlier' },
  { type: 'professional', label: 'Professional' },
  { type: 'regenerate', label: 'Regenerate' },
];

function RefinementButtons({
  websiteId: _websiteId,
  elementLabel: _elementLabel,
  editorRef: _editorRef,
  refiningType,
  refineError,
  onRefine,
}: {
  websiteId: string;
  elementLabel: string;
  editorRef: React.RefObject<HTMLDivElement | null>;
  refiningType: RefinementType | null;
  refineError: string;
  onRefine: (type: RefinementType) => void;
}) {
  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <div className="flex items-center gap-1.5 mb-1.5">
        <svg className="w-3.5 h-3.5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
        <span className="text-xs font-medium text-gray-500">AI Refine</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {REFINEMENT_OPTIONS.map(({ type, label }) => (
          <button
            key={type}
            onClick={() => onRefine(type)}
            disabled={refiningType !== null}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {refiningType === type && (
              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {label}
          </button>
        ))}
      </div>
      {refineError && (
        <p className="text-xs text-red-500 mt-1">{refineError}</p>
      )}
    </div>
  );
}

// =============================================================================
// COMPONENT
// =============================================================================

export function EditModal({
  elementKind,
  currentValue,
  label,
  websiteId,
  elementLabel,
  mode = 'admin',
  onSave,
  onClose,
}: EditModalProps) {
  const [imageUrl, setImageUrl] = useState(currentValue);
  const [buttonText, setButtonText] = useState(currentValue);
  const [refiningType, setRefiningType] = useState<RefinementType | null>(null);
  const [refineError, setRefineError] = useState('');
  const editorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const config = KIND_CONFIG[elementKind] || KIND_CONFIG.text;
  const isRichText = supportsFormatting(elementKind);
  const showRefinement = isRichText && websiteId && mode !== 'customer';

  // Focus on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isRichText && editorRef.current) {
        editorRef.current.focus();
        // Place cursor at end
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        sel?.removeAllRanges();
        sel?.addRange(range);
      } else if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Get current value from the editor
  const getCurrentValue = useCallback((): string => {
    if (isRichText && editorRef.current) {
      return editorRef.current.innerHTML.trim();
    }
    if (elementKind === 'image') return imageUrl.trim();
    return buttonText.trim();
  }, [isRichText, elementKind, imageUrl, buttonText]);

  // Save handler
  const handleSave = useCallback(() => {
    const newValue = getCurrentValue();
    // Strip down to plain text for comparison to detect actual content changes
    const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '').trim();
    const hasContentChange = isRichText
      ? newValue !== currentValue // Compare full HTML for rich text
      : stripHtml(newValue) !== stripHtml(currentValue);

    if (newValue && hasContentChange) {
      onSave(newValue);
    }
    onClose();
  }, [getCurrentValue, currentValue, isRichText, onSave, onClose]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      // ⌘+Enter to save (always)
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSave();
        return;
      }
      // Enter to save for non-rich-text single-line inputs
      if (e.key === 'Enter' && !e.shiftKey && !isRichText && elementKind !== 'text') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, onClose, isRichText, elementKind]);

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center text-sm font-semibold">
              {config.icon}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{label}</h3>
              <p className="text-xs text-gray-400 capitalize">{elementKind}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          {elementKind === 'image' ? (
            /* ── IMAGE EDITOR ── */
            <>
              {imageUrl && (
                <div className="mb-3 rounded-lg overflow-hidden bg-gray-50 border border-gray-200">
                  <img
                    src={imageUrl}
                    alt="Preview"
                    className="w-full h-40 object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
              <input
                ref={inputRef}
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder={config.placeholder}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
              />
              <p className="text-xs text-gray-400 mt-1.5">
                Paste an image URL. Media library coming soon.
              </p>
            </>
          ) : isRichText ? (
            /* ── RICH TEXT EDITOR (heading / text) ── */
            <>
              <div className="border border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-shadow overflow-hidden">
                <FormattingToolbar editorRef={editorRef} />
                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  dangerouslySetInnerHTML={{ __html: currentValue }}
                  data-placeholder={config.placeholder}
                  className={`px-3 py-2.5 text-sm outline-none min-h-[${elementKind === 'text' ? '80px' : '40px'}] max-h-[300px] overflow-y-auto`}
                  style={{
                    minHeight: elementKind === 'text' ? '80px' : '40px',
                  }}
                />
              </div>
              <div className="flex justify-between mt-1.5">
                <p className="text-xs text-gray-400">
                  <span className="font-medium">⌘B</span> bold ·{' '}
                  <span className="font-medium">⌘I</span> italic ·{' '}
                  <span className="font-medium">⌘U</span> underline
                </p>
                <p className="text-xs text-gray-400">⌘+Enter to save</p>
              </div>
              {showRefinement && (
                <RefinementButtons
                  websiteId={websiteId}
                  elementLabel={elementLabel || label}
                  editorRef={editorRef}
                  refiningType={refiningType}
                  refineError={refineError}
                  onRefine={async (type: RefinementType) => {
                    if (refiningType || !editorRef.current) return;
                    setRefiningType(type);
                    setRefineError('');
                    try {
                      const currentText = editorRef.current.innerHTML.trim();
                      const result = await refineText(
                        websiteId,
                        currentText,
                        type,
                        elementLabel || label
                      );
                      if (result.success && editorRef.current) {
                        editorRef.current.innerHTML = result.refined;
                      }
                    } catch (err) {
                      setRefineError(err instanceof Error ? err.message : 'Refinement failed');
                    } finally {
                      setRefiningType(null);
                    }
                  }}
                />
              )}
            </>
          ) : (
            /* ── PLAIN TEXT INPUT (button) ── */
            <>
              <input
                ref={inputRef}
                type="text"
                value={buttonText}
                onChange={(e) => setButtonText(e.target.value)}
                placeholder={config.placeholder}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
              />
              <div className="flex justify-end mt-1.5">
                <p className="text-xs text-gray-400">{buttonText.length} chars</p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
          <p className="text-xs text-gray-400">
            {isRichText ? '⌘+Enter to save · Esc to cancel' : 'Enter to save · Esc to cancel'}
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3.5 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-3.5 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
